import type {IModifyTemplateGraph} from "@cm/template-nodes"
import type {
    InitStackableMattressStandardModelMasterTemplateOptions,
    InitMattressInstanceTemplateOptions,
} from "#mattress/assets-definitions/standard-models/common/model-factory"
import {
    initInstanceTemplateCommon,
    initStackableMattressStandardModelMasterTemplate,
    getDefaultHandlesParameters,
} from "#mattress/assets-definitions/standard-models/common/model-factory"
import {
    getComponentGraphNodesForBottomPanel,
    getComponentGraphNodesForTopPanel,
    getComponentGraphNodesForNonStackableMiddlePart,
} from "#mattress/assets-definitions/standard-models/common/parts"
import {getComponentGraphNodesForNonStackableStandardModelDimensionsLogic} from "#mattress/assets-definitions/standard-models/common/dimension-logic"
import {createTemplateReference} from "#mattress/assets-definitions/common"
import {createMattressGroup, createMattress, createMattressMesh} from "#mattress/assets-definitions/create-mattress"
import {getComponentGraphForTopAndBottomPartMaterialOffsetUtilityTemplate} from "#mattress/assets-definitions/standard-models/common/material-offset"

const reversedWaterfallDefaults: InitStackableMattressStandardModelMasterTemplateOptions = {
    openingOnBottom: false,
    topPartFabricLayout: "Waterfall",
    bottomPartFabricLayout: "Border",
    middlePartFabricLayout: "Border",
}

export function initReversedWaterfallMasterTemplate(
    modifyTemplateGraph: IModifyTemplateGraph,
    options: InitStackableMattressStandardModelMasterTemplateOptions = {openingOnBottom: false},
) {
    initStackableMattressStandardModelMasterTemplate(modifyTemplateGraph, {...reversedWaterfallDefaults, ...options, bottomPanelMode: "edge"})
}

export function initReversedWaterfallInstanceTemplate(
    modifyTemplateGraph: IModifyTemplateGraph,
    masterTemplateDataLegacyId: number,
    options: InitMattressInstanceTemplateOptions,
) {
    const optionsParameters = {
        ...(options.parameters ? options.parameters : {}),
        width: 90,
        length: 200,
        "top-height": 1,
        "bottom-height": 24,
        "corner-radius": 3,
        "top-radius": 1,
        "bottom-radius": 4,
        "layer-count": 0,
        "accessories-bottom-curve/accessory-count": 1,
        "accessories-bottom-curve/accessory-stack/accessory-0/accessory": createTemplateReference(options.accessoriesTemplatesLegacyIds["piping 5.5mm"]),
        "accessories-bottom-panel/accessory-count": 1,
        "accessories-bottom-panel/accessory-stack/accessory-0/accessory": createTemplateReference(options.accessoriesTemplatesLegacyIds["zipper Teeth, M5"]),
        "accessories-bottom-panel/accessory-stack/accessory-0/accessory/puller-0/puller-template": createTemplateReference(
            options.accessoriesTemplatesLegacyIds["puller Standard Puller (YKK, n56) with Slider, M5"],
        ),
        "accessories-bottom-panel/accessory-stack/accessory-0/accessory/puller-0/puller-offset": 0.995,
        ...getDefaultHandlesParameters(options),
    }

    initInstanceTemplateCommon(
        modifyTemplateGraph,
        {
            legacyId: masterTemplateDataLegacyId,
            instanceName: "Reversed Waterfall (Master)",
            instanceId: "reversed-waterfall-master",
        },
        {
            ...options,
            parameters: optionsParameters,
        },
    )
}

export function initReversedWaterfallWithMirrorMasterTemplate(
    modifyTemplateGraph: IModifyTemplateGraph,
    options: InitStackableMattressStandardModelMasterTemplateOptions = {openingOnBottom: true},
) {
    initStackableMattressStandardModelMasterTemplate(modifyTemplateGraph, {
        ...reversedWaterfallDefaults,
        bottomPartFabricLayout: "BorderWithMirror",
        ...options,
        bottomPanelMode: "mirror",
    })
}

export function initReversedWaterfallWithMirrorInstanceTemplate(
    modifyTemplateGraph: IModifyTemplateGraph,
    masterTemplateDataLegacyId: number,
    options: InitMattressInstanceTemplateOptions,
) {
    const optionsParameters = {
        ...(options.parameters ? options.parameters : {}),
        width: 90,
        length: 200,
        "top-height": 1,
        "bottom-height": 24,
        "corner-radius": 3,
        "top-radius": 1,
        "bottom-radius": 4,
        "layer-count": 0,
        "accessories-bottom-curve/accessory-count": 1,
        "accessories-bottom-curve/accessory-stack/accessory-0/accessory": createTemplateReference(options.accessoriesTemplatesLegacyIds["piping 5.5mm"]),
        "accessories-bottom-panel/accessory-count": 1,
        "accessories-bottom-panel/accessory-stack/accessory-0/accessory": createTemplateReference(options.accessoriesTemplatesLegacyIds["zipper Teeth, M5"]),
        "accessories-bottom-panel/accessory-stack/accessory-0/accessory/puller-0/puller-template": createTemplateReference(
            options.accessoriesTemplatesLegacyIds["puller Standard Puller (YKK, n56) with Slider, M5"],
        ),
        "accessories-bottom-panel/accessory-stack/accessory-0/accessory/puller-0/puller-offset": 0.995,
        ...getDefaultHandlesParameters(options),
    }

    initInstanceTemplateCommon(
        modifyTemplateGraph,
        {
            legacyId: masterTemplateDataLegacyId,
            instanceName: "Reversed Waterfall with mirror (Master)",
            instanceId: "reversed-waterfall-with-mirror-master",
        },
        {
            ...options,
            parameters: optionsParameters,
        },
    )
}

export function initReversedWaterfallWithMirrorOnTopAndBottomMasterTemplate(
    modifyTemplateGraph: IModifyTemplateGraph,
    options: InitStackableMattressStandardModelMasterTemplateOptions = {openingOnBottom: true},
) {
    const optionsWithDefaults: InitStackableMattressStandardModelMasterTemplateOptions = {
        middlePartFabricLayout: "BorderWithMirror",
        ...options,
    }

    const dimensions = getComponentGraphNodesForNonStackableStandardModelDimensionsLogic()

    const topAndBottomPartMaterialOffsetComponentGraph = getComponentGraphForTopAndBottomPartMaterialOffsetUtilityTemplate()
    const {nodes: bottomPanelNodes, curve: bottomPanelCurve} = getComponentGraphNodesForBottomPanel({
        defaultBottomPanelMaterialLegacyId: optionsWithDefaults.bottomPartDefaultMaterialLegacyId,
        mode: "mirror",
        topAndBottomPartMaterialOffsetComponentGraph,
        width: dimensions.width,
        length: dimensions.length,
    })
    const bottomPanelGroup = createMattressGroup("Bottom Panel", [...bottomPanelNodes])

    const {nodes: topPanelNodes, curve: topPanelCurve} = getComponentGraphNodesForTopPanel({
        defaultTopPanelMaterialLegacyId: optionsWithDefaults.topPartDefaultMaterialLegacyId,
        mode: "mirror",
        topAndBottomPartMaterialOffsetComponentGraph,
        width: dimensions.width,
        length: dimensions.length,
    })
    const topPanelGroup = createMattressGroup("Top Panel", [...topPanelNodes])

    const {nodes: middlePartNodes} = getComponentGraphNodesForNonStackableMiddlePart({
        bottomPanelCurve,
        topPanelCurve,
        defaultMaterialLegacyId: optionsWithDefaults.middlePartDefaultMaterialLegacyId,
        fabricLayout: optionsWithDefaults.middlePartFabricLayout,
    })
    const middlePartGroup = createMattressGroup("Middle Part", middlePartNodes)

    const mattressMesh = createMattressMesh("Mattress Mesh", {
        width: dimensions.width,
        length: dimensions.length,
        height: dimensions.height,
        cornerRadius: dimensions.cornerRadius,
        topRadius: dimensions.safeTopRadius,
        bottomRadius: dimensions.safeBottomRadius,
    })
    const dimensionsGroup = createMattressGroup("Dimensions", [
        dimensions.width,
        dimensions.length,
        dimensions.height,
        dimensions.cornerRadius,
        dimensions.topRadius,
        dimensions.bottomRadius,
        dimensions.safeIntermediateBottomRadius,
        dimensions.safeIntermediateTopRadius,
        dimensions.safeBottomRadius,
        dimensions.safeTopRadius,
    ])

    const mattress = createMattress([mattressMesh, dimensionsGroup, topPanelGroup, middlePartGroup, bottomPanelGroup])

    modifyTemplateGraph.modifyTemplateGraph((graph) => {
        graph.parameters.nodes.updateParameters({list: [mattress]})
    })
}

export function initReversedWaterfallWithMirrorOnTopAndBottomInstanceTemplate(
    modifyTemplateGraph: IModifyTemplateGraph,
    masterTemplateDataLegacyId: number,
    options: InitMattressInstanceTemplateOptions,
) {
    const optionsParameters = {
        ...(options.parameters ? options.parameters : {}),
        width: 90,
        length: 200,
        height: 30,
        "corner-radius": 3,
        "top-radius": 1,
        "bottom-radius": 1,
        ...getDefaultHandlesParameters(options),
    }

    initInstanceTemplateCommon(
        modifyTemplateGraph,
        {
            legacyId: masterTemplateDataLegacyId,
            instanceName: "Reversed Waterfall with mirror on top and bottom (Master)",
            instanceId: "reversed-waterfall-with-mirror-on-top-and-bottom-master",
        },
        {
            ...options,
            parameters: optionsParameters,
        },
    )
}
